import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CreditCard, Search } from "lucide-react";
import { format } from "date-fns";

type Candidate = {
  booking_id: string;
  studio_name: string;
  start_at: string;
  end_at: string;
  total: number;
  checkout_url: string;
  expires_at: string | null;
};

export function ResumeCheckoutBanner({ initialEmail = "" }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  const lookup = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await supabase.functions.invoke("booking-resume-candidates", {
        body: { email: email.trim() },
      });
      setCandidates((data?.candidates as Candidate[]) ?? []);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-8 bg-panel border border-border p-4">
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-4 h-4 text-primary" />
        <p className="font-display tracking-widest text-xs uppercase">Resume a pending checkout</p>
      </div>
      <p className="text-xs text-muted-foreground font-body mb-3">
        Already started a booking? Enter the email you used and we'll pick up where you left off — no need to re-select the slot.
      </p>
      <div className="flex flex-wrap gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="max-w-xs"
        />
        <Button onClick={lookup} disabled={loading || !email.trim()} variant="outline" size="sm">
          {loading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Search className="w-3 h-3 mr-2" />}
          Find my booking
        </Button>
      </div>

      {searched && !loading && candidates.length === 0 && (
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-3">
          No pending checkouts found in the last hour.
        </p>
      )}

      {candidates.length > 0 && (
        <div className="mt-3 space-y-2">
          {candidates.map((c) => (
            <div key={c.booking_id} className="border border-primary/40 bg-primary/5 p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="font-body text-sm">{c.studio_name}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {format(new Date(c.start_at), "EEE, MMM d · h:mma")} → {format(new Date(c.end_at), "h:mma")}
                </p>
              </div>
              <span className="font-display text-primary">${c.total.toFixed(2)}</span>
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <a href={c.checkout_url} target="_top" rel="noopener noreferrer">Resume payment →</a>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}